import { useCallback, useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import DOMPurify from 'dompurify';
import * as Sentry from '@sentry/react';
import { repairFlowchart, repairMermaid, buildRegenerateMessage } from '@blueprint/shared';

// Custom `themeVariables` were removed deliberately. Mermaid v11's C4
// renderer (and a handful of other lazy-loaded diagram types) read theme
// variables through a pipeline distinct from the flowchart/sequence/ER
// renderers. When our overrides only set the flowchart-flavoured
// variables (primaryColor, primaryTextColor, primaryBorderColor, etc.)
// the C4 renderer's internal lookup hits an undefined slot and throws
// `Cannot read properties of undefined (reading 'primaryColor')` during
// its first lazy-load. It fires during lazy diagram-type bootstrap
// inside `mermaid.parse` or `mermaid.render`, and the resulting promise
// rejection can escape our component-level try/catch via the unhandled-
// rejection path, surfacing as a red page-wide banner on the chat
// surface or an amber "Diagram syntax error" inline panel on SAD/
// architecture pages.
//
// Letting Mermaid use its built-in `default` theme (no themeVariables
// overrides) keeps every diagram type working without us having to
// maintain a per-type variable table. The cost is purely cosmetic — our
// previous palette was a Google-ish blue; diagrams now render with
// Mermaid's stock palette. (The .docx export already used Mermaid's
// stock palette via mermaid.ink, which runs server-side with its own
// theme config — never received our overrides — so removing them
// actually makes the browser render and the .docx export consistent.)
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'Arial, sans-serif',
  flowchart: { useMaxWidth: true, htmlLabels: true, curve: 'basis' },
  er: { useMaxWidth: true },
  sequence: { useMaxWidth: true },
});

const DOMPURIFY_CONFIG: Parameters<typeof DOMPurify.sanitize>[1] = {
  USE_PROFILES: { svg: true, svgFilters: true },
  ADD_TAGS: ['style', 'foreignObject'],
  ADD_ATTR: [
    'xmlns', 'viewBox', 'class', 'style', 'transform',
    'fill', 'stroke', 'font-size', 'font-family', 'font-weight',
    'text-anchor', 'dominant-baseline',
    'marker-end', 'marker-start',
    'd', 'cx', 'cy', 'r', 'rx', 'ry',
    'x', 'y', 'x1', 'y1', 'x2', 'y2',
    'width', 'height', 'points', 'dx', 'dy',
    'requiredFeatures', 'overflow',
  ],
};

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

function clampZoom(z: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(z / ZOOM_STEP) * ZOOM_STEP));
}

// HOTFIX-S4C-1113 — parse intrinsic SVG dimensions from the rendered viewBox so
// the scrollable wrapper can be sized to (width × zoom, height × zoom). This is
// the primary path; the fallback (getBoundingClientRect on the SVG element) runs
// from a useEffect if parsing fails — see Q4 adjudication. Without this sizing,
// the native scrollbar has nothing to track and the overflow:auto switch alone
// produces no scrollbars (the transform-based zoom doesn't expand the box-model).
function parseSvgDims(svgString: string): { width: number; height: number } | null {
  const match = svgString.match(/viewBox\s*=\s*["']([^"']+)["']/i);
  if (!match) return null;
  const parts = match[1].trim().split(/[\s,]+/).map(Number);
  if (parts.length !== 4 || parts.some(n => !Number.isFinite(n))) return null;
  const [, , w, h] = parts;
  if (w <= 0 || h <= 0) return null;
  return { width: w, height: h };
}


// Per-dialect Mermaid late-rescue (L3). Mirrors the server-side dispatch shape
// in apps/api/src/services/mermaidSanitizer.ts so the two layers agree on what
// each diagram type's repairs look like. Frontend duplicates the detector
// locally because the worktree's @blueprint/shared junction can't see new
// shared exports until merge to main — F1-bis (#1118) consolidates post-merge.
type MermaidDialect = 'flowchart' | 'c4' | 'er' | 'sequence' | 'journey' | 'unknown';

function detectDialect(code: string): MermaidDialect {
  const firstLine = code.split('\n').find(l => l.trim().length > 0)?.trim().toLowerCase() ?? '';
  if (/^(graph|flowchart)\b/.test(firstLine)) return 'flowchart';
  // Mermaid accepts YAML front matter and `%%` directives before the keyword.
  // Only flowcharts are upgraded this way: that is the dialect with a safe repair.
  const lines = code
    .replace(/^\s*---[ \t]*\r?\n[\s\S]*?\r?\n---[ \t]*(?:\r?\n|$)/, '')
    .split('\n');
  const at = lines.findIndex(l => l.trim() !== '' && !l.trim().startsWith('%%'));
  const body = at === -1 ? '' : lines[at].trim().toLowerCase();
  if (/^(graph|flowchart)\b/.test(body)) return 'flowchart';
  if (/^c4(context|container|component|dynamic|deployment)\b/.test(firstLine)) return 'c4';
  if (/^erdiagram\b/.test(firstLine)) return 'er';
  if (/^sequencediagram\b/.test(firstLine)) return 'sequence';
  if (/^journey\b/.test(firstLine)) return 'journey';
  return 'unknown';
}

/**
 * L3 late-rescue: attempt to fix common Mermaid syntax issues that escaped
 * L2's server-side sanitiser. Returns the cleaned code, or the original if
 * no changes needed.
 *
 * Sequence-only aggressive fixes are retained as the L3 rescue path for the
 * pre-#1114 #597 era. Flowchart repairs (subgraph ids for arrows that name a subgraph
 * by title, quoted labels, reserved-word `end`) come from the shared
 * repairFlowchart, the same function the server applies at L2. Diagrams saved
 * before L2 had those repairs are fixed here, and show "Auto-corrected".
 */
function autoFixMermaidSyntax(raw: string): string {
  let code = raw.replace(/\\n/g, ' ');
  const dialect = detectDialect(code);
  if (dialect === 'sequence') {
    code = code.replace(/"([^"\n]+)"/g, '$1');
    code = code.replace(/\{([^}\n]+)\}/g, '[$1]');
    code = code.replace(/(participant\s+\w+\s+as\s+[^(\n]+?)\s*\([^)\n]*\)/gi, '$1');
  }
  if (dialect === 'flowchart') {
    // Late rescue for flowcharts saved before the server-side repair existed
    // (and for anything it still misses). Same shared function as the server.
    code = repairFlowchart(code);
  }
  if (dialect === 'sequence' || dialect === 'er' || dialect === 'c4' || dialect === 'journey' || dialect === 'unknown') {
    // #3716: reproduced parse-error shapes for these dialects (shared with the
    // server). Runs after the legacy sequence rescue above.
    code = repairMermaid(code);
  }
  return code;
}

interface MermaidDiagramProps {
  code: string;
  className?: string;
  /**
   * #1117: sends the regenerate hint to the agent. Omitted on read-only or
   * streaming views, where the action stays disabled.
   */
  onRegenerate?: (message: string) => void;
}

export default function MermaidDiagram({ code, className, onRegenerate }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  // Pan-drag start: captures initial cursor + initial scroll position so move
  // events can compute new scroll = initial - cursor delta. Replaces the
  // pre-#1113 (mx,my,px,py) shape — px/py were transform offsets, now sl/st are
  // viewport.scrollLeft/Top at drag start.
  const panStartRef = useRef<{ mx: number; my: number; sl: number; st: number } | null>(null);
  // Wheel-zoom anchor: captured at wheel event, applied in useEffect[zoom] after
  // the wrapper has re-rendered with new dimensions. This preserves the Q3
  // invariant — diagram pixel under the cursor stays under the cursor across
  // zoom changes. Cannot apply scrollLeft/Top synchronously because the wrapper
  // hasn't grown yet; the browser would clamp to the pre-zoom maxScroll.
  const pendingAnchorRef = useRef<{
    mx: number;
    my: number;
    oldZoom: number;
    oldScrollLeft: number;
    oldScrollTop: number;
  } | null>(null);

  const [svg, setSvg] = useState<string>('');
  const [svgDims, setSvgDims] = useState<{ width: number; height: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoCorrected, setAutoCorrected] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [regenRequested, setRegenRequested] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);

  useEffect(() => {
    if (!code.trim()) return;
    let cancelled = false;

    function sweepOrphanedMermaidElements() {
      document.querySelectorAll('body > svg[aria-roledescription="error"]').forEach(el => el.remove());
      document.querySelectorAll('body > svg:not([class])').forEach(el => {
        if (el.textContent?.includes('Syntax error')) el.remove();
      });
      document.querySelectorAll('body > .error-icon').forEach(el => el.remove());
    }

    async function tryRender(mermaidCode: string): Promise<string | null> {
      try {
        await mermaid.parse(mermaidCode);
      } catch (error) {
        // TEMP DIAGNOSTIC (do not commit) — tracking issue: revert once the
        // intermittent render failure's root cause is found and fixed.
        Sentry.captureException(error, {
          tags: { source: 'mermaid-diagnostic', stage: 'parse' },
          extra: {
            mermaidCode: JSON.stringify(mermaidCode),
            timestamp: new Date().toISOString(),
          },
        });
        sweepOrphanedMermaidElements();
        return null;
      }
      const id = `mermaid-${Math.random().toString(36).substring(2, 9)}`;
      try {
        const { svg: renderedSvg } = await mermaid.render(id, mermaidCode);
        const sanitized = DOMPurify.sanitize(renderedSvg, DOMPURIFY_CONFIG);
        return sanitized.trim() ? sanitized : null;
      } catch (error) {
        // TEMP DIAGNOSTIC (do not commit) — tracking issue: revert once the
        // intermittent render failure's root cause is found and fixed.
        const target = document.getElementById(id);
        const rect = target?.getBoundingClientRect();
        const computed = target ? window.getComputedStyle(target) : null;
        Sentry.captureException(error, {
          tags: { source: 'mermaid-diagnostic', stage: 'render' },
          extra: {
            mermaidCode: JSON.stringify(mermaidCode),
            diagramId: id,
            domTargetExists: !!target,
            domTargetRect: rect
              ? { width: rect.width, height: rect.height, top: rect.top, left: rect.left }
              : null,
            domTargetVisible: computed
              ? { display: computed.display, visibility: computed.visibility }
              : null,
            timestamp: new Date().toISOString(),
          },
        });
        return null;
      } finally {
        const el = document.getElementById(id);
        if (el && el.parentElement === document.body) el.remove();
        const errorEl = document.getElementById(`d${id}`);
        if (errorEl) errorEl.remove();
        sweepOrphanedMermaidElements();
      }
    }

    function commitRender(result: string, corrected: boolean) {
      setSvg(result);
      setError(null);
      setAutoCorrected(corrected);
      setZoom(1);
      // svgDims primary path — parse viewBox synchronously. If absent/malformed,
      // null triggers the getBoundingClientRect fallback effect (Q4 safety net).
      setSvgDims(parseSvgDims(result));
    }

    async function render() {
      // Try original code first
      const originalResult = await tryRender(code.trim());
      if (!cancelled && originalResult) {
        commitRender(originalResult, false);
        return;
      }

      // Original failed — try auto-fix
      const fixed = autoFixMermaidSyntax(code.trim());
      if (fixed !== code.trim()) {
        const fixedResult = await tryRender(fixed);
        if (!cancelled && fixedResult) {
          commitRender(fixedResult, true);
          return;
        }
      }

      // Both failed — show error
      if (!cancelled) {
        let errorMsg = 'Invalid diagram syntax';
        try {
          await mermaid.parse(code.trim());
        } catch (parseErr: unknown) {
          if (parseErr instanceof Error) errorMsg = parseErr.message;
        }
        setError(errorMsg);
        setSvg('');
        setSvgDims(null);
        setAutoCorrected(false);
      }
      sweepOrphanedMermaidElements();
    }

    // Defence against the unhandled-rejection escape path: if Mermaid's
    // lazy diagram-type bootstrap throws during `parse`/`render` before
    // our inner try/catch can intercept it (the path that surfaced the
    // C4 + primaryColor TypeError as a page-wide banner), a bare
    // `void render()` would let the rejection bubble to
    // `window.onunhandledrejection`. Setting the local error state
    // localises the failure to this diagram's amber panel instead.
    render().catch(() => {
      if (!cancelled) setError('Diagram failed to initialise');
    });
    return () => {
      cancelled = true;
      document.querySelectorAll('body > svg[aria-roledescription="error"]').forEach(el => el.remove());
    };
  }, [code]);

  // Q4 fallback: when viewBox is absent or malformed (defensive against future
  // Mermaid output changes), measure the rendered SVG element directly. Runs
  // only when svgDims is null but svg is present; the natural-size measurement
  // is taken at zoom=1 (always the case immediately after render — commitRender
  // resets zoom). Two-stage fallback to honor the Q4 mandate "never let a
  // viewBox-less SVG produce a permanently unsized wrapper":
  //   stage A: measure the SVG element via getBoundingClientRect
  //   stage B (only if A returns zero — e.g., width="100%" in a collapsed
  //           parent): use the viewport's own client dimensions as a
  //           graceful-degradation default. The diagram fills the viewport;
  //           no overflow scroll, but the surface is functional rather than
  //           silently broken.
  useEffect(() => {
    if (svgDims || !svg || !containerRef.current) return;
    const svgEl = containerRef.current.querySelector('svg');
    if (!svgEl) return;
    const rect = svgEl.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setSvgDims({ width: rect.width, height: rect.height });
      return;
    }
    const viewport = viewportRef.current;
    if (viewport && viewport.clientWidth > 0 && viewport.clientHeight > 0) {
      setSvgDims({ width: viewport.clientWidth, height: viewport.clientHeight });
    }
  }, [svg, svgDims]);

  // Reset scroll position when a new diagram renders. Lives in its own effect
  // because the render effect's setSvg/setZoom batch doesn't have the new
  // wrapper dimensions yet — scrollLeft=0 here runs after commit, when the
  // viewport reflects the new sized wrapper.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (viewport && svg) {
      viewport.scrollLeft = 0;
      viewport.scrollTop = 0;
    }
  }, [svg]);

  // Q3 anchor restoration: after a wheel-driven zoom change, adjust scroll so
  // the diagram pixel under the cursor pre-zoom stays under the cursor post-zoom.
  // Math: a point at viewport-relative cursor (mx, my) is at scrollable-area
  // coordinate (oldScroll + cursor). After zoom by ratio = newZoom/oldZoom, that
  // same diagram point is now at (oldScroll + cursor) × ratio in the new
  // scrollable area. To keep it under the cursor, newScroll = that - cursor.
  // Skipped when pendingAnchorRef is null (zoom change came from button/reset,
  // which intentionally don't anchor — see Q3 implementation note).
  useEffect(() => {
    const anchor = pendingAnchorRef.current;
    const viewport = viewportRef.current;
    if (!anchor || !viewport) return;
    const ratio = zoom / anchor.oldZoom;
    viewport.scrollLeft = (anchor.oldScrollLeft + anchor.mx) * ratio - anchor.mx;
    viewport.scrollTop = (anchor.oldScrollTop + anchor.my) * ratio - anchor.my;
    pendingAnchorRef.current = null;
  }, [zoom]);

  const resetView = useCallback(() => {
    pendingAnchorRef.current = null;
    setZoom(1);
    const viewport = viewportRef.current;
    if (viewport) {
      viewport.scrollLeft = 0;
      viewport.scrollTop = 0;
    }
  }, []);

  // Native event type (not React.WheelEvent) — this handler is attached
  // imperatively in the effect below with { passive: false } so e.preventDefault()
  // actually suppresses the page scroll. React's onWheel prop registers as
  // passive by default (React 17+), which silently no-ops preventDefault and
  // would cause the page to scroll while the diagram zooms.
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const viewport = viewportRef.current;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    setZoom(prev => {
      const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      const newZoom = clampZoom(prev + delta);
      if (newZoom === prev) {
        // Clamped at min/max — no zoom change, no anchor (prevents a stale
        // anchor from firing against a later button zoom).
        pendingAnchorRef.current = null;
        return prev;
      }
      pendingAnchorRef.current = {
        mx,
        my,
        oldZoom: prev,
        oldScrollLeft: viewport.scrollLeft,
        oldScrollTop: viewport.scrollTop,
      };
      return newZoom;
    });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    panStartRef.current = {
      mx: e.clientX,
      my: e.clientY,
      sl: viewport.scrollLeft,
      st: viewport.scrollTop,
    };
    setIsPanning(true);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!panStartRef.current) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    const { mx, my, sl, st } = panStartRef.current;
    // Drag-to-pan: dragging right (cursor moves +x) reveals content to the LEFT,
    // which means scrolling LEFT (decreasing scrollLeft). Hence subtract delta.
    viewport.scrollLeft = sl - (e.clientX - mx);
    viewport.scrollTop = st - (e.clientY - my);
  }, []);

  const handleMouseUp = useCallback(() => {
    panStartRef.current = null;
    setIsPanning(false);
  }, []);

  // Touch handlers mirror mouse — single-touch only. Multi-touch (pinch-zoom,
  // two-finger pan) falls through to the browser via the length-check
  // early-returns. touchstart/end stay as React props (no preventDefault needed
  // — preventing touchstart kills click events on children). touchmove is
  // attached imperatively with { passive: false } so e.preventDefault() can
  // suppress page scroll during diagram pan (React's onTouchMove is passive by
  // default per React 17+ same as onWheel).
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    const touch = e.touches[0];
    panStartRef.current = {
      mx: touch.clientX,
      my: touch.clientY,
      sl: viewport.scrollLeft,
      st: viewport.scrollTop,
    };
    setIsPanning(true);
  }, []);

  // Native TouchEvent type — attached imperatively in the effect below.
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!panStartRef.current || e.touches.length !== 1) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    e.preventDefault();
    const touch = e.touches[0];
    const { mx, my, sl, st } = panStartRef.current;
    viewport.scrollLeft = sl - (touch.clientX - mx);
    viewport.scrollTop = st - (touch.clientY - my);
  }, []);

  const handleTouchEnd = useCallback(() => {
    panStartRef.current = null;
    setIsPanning(false);
  }, []);

  // Imperative non-passive listener attachment for wheel + touchmove. React's
  // onWheel/onTouchMove props register passive listeners (React 17+ default),
  // so e.preventDefault() inside them is silently ignored — that's the root
  // cause of CRITICAL-1 (touch drag scrolls page) and MEDIUM-2 (wheel zoom
  // scrolls page). Imperative addEventListener with { passive: false } restores
  // preventDefault semantics. Re-runs when `svg` changes because viewportRef
  // doesn't exist during the loading/error early-returns and only becomes
  // attached once the main render path runs.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.addEventListener('wheel', handleWheel, { passive: false });
    viewport.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      viewport.removeEventListener('wheel', handleWheel);
      viewport.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleWheel, handleTouchMove, svg]);

  const handleDoubleClick = useCallback(() => {
    resetView();
  }, [resetView]);

  // Return null for blank code after all hooks have been called
  if (!code.trim()) return null;

  if (error) {
    return (
      <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 my-4 text-sm">
        <span className="text-amber-700">⚠ Diagram syntax error</span>
        <button
          onClick={() => setShowCode(!showCode)}
          className="ml-2 text-xs text-amber-500 underline"
        >
          {showCode ? 'Hide code' : 'View code'}
        </button>
        {/* #1117 — Regenerate: shown only after the L2 server sanitiser and the L3 repair
            above have both failed. One click per failure; the server enforces the real cap (429). */}
        <button
          type="button"
          disabled={!onRegenerate || regenRequested}
          onClick={() => {
            if (!onRegenerate) return;
            setRegenRequested(true);
            onRegenerate(buildRegenerateMessage(code, error));
          }}
          title={
            !onRegenerate
              ? 'Regenerate is not available in this view'
              : regenRequested
                ? 'Regeneration requested — see the agent reply'
                : 'Ask the agent to rewrite this diagram'
          }
          className={`ml-2 text-xs underline ${!onRegenerate || regenRequested ? 'text-amber-300 cursor-not-allowed' : 'text-amber-600'}`}
        >
          {regenRequested ? 'Regeneration requested' : 'Regenerate diagram'}
        </button>
        {showCode && (
          <pre className="mt-2 bg-amber-100 p-2 rounded text-xs overflow-x-auto">
            <code>{code}</code>
          </pre>
        )}
      </div>
    );
  }

  if (!svg) {
    // Loading state while mermaid renders
    return (
      <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 my-4 animate-pulse h-24" />
    );
  }

  return (
    <div className={`my-4 ${className ?? ''}`}>
      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
        {/* Viewport — overflow:auto enables native scrollbars. The sized wrapper
            (below) gives them dimensions to track; without it the transform-only
            zoom doesn't expand the box-model and scrollbars never appear. */}
        <div
          ref={viewportRef}
          data-testid="mermaid-viewport"
          className="relative overflow-auto bg-white"
          style={{ maxHeight: 500, cursor: isPanning ? 'grabbing' : 'grab' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onDoubleClick={handleDoubleClick}
        >
          {/* Sized wrapper — width/height = svg natural × zoom. This is what
              gives the scrollbar something to track. Without explicit dimensions
              here, transform:scale on the inner doesn't expand layout. */}
          <div
            data-testid="mermaid-sized-wrapper"
            className="p-4"
            style={{
              width: svgDims ? svgDims.width * zoom : undefined,
              height: svgDims ? svgDims.height * zoom : undefined,
            }}
          >
            <div
              ref={containerRef}
              className="mermaid-container"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: '0 0',
                userSelect: 'none',
                width: svgDims?.width,
                height: svgDims?.height,
              }}
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          </div>
        </div>

        {/* Controls bar */}
        <div className="flex items-center justify-between border-t border-gray-100 px-3 py-1.5 bg-gray-50">
          {/* Zoom controls */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setZoom(z => clampZoom(z - ZOOM_STEP))}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 text-gray-500 text-sm font-medium"
              title="Zoom out"
            >
              −
            </button>
            <span className="text-xs text-gray-500 w-10 text-center tabular-nums">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => setZoom(z => clampZoom(z + ZOOM_STEP))}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 text-gray-500 text-sm font-medium"
              title="Zoom in"
            >
              +
            </button>
            <button
              onClick={resetView}
              className="ml-1 text-xs text-gray-400 hover:text-gray-600 px-1.5 py-0.5 rounded hover:bg-gray-200"
              title="Reset view"
            >
              Reset
            </button>
          </div>

          {/* Secondary controls */}
          <div className="flex items-center gap-2">
            {autoCorrected && (
              <span className="text-xs text-amber-500" title="Minor syntax issues were automatically corrected for rendering">
                Auto-corrected
              </span>
            )}
            <button
              onClick={() => setShowCode(!showCode)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              {showCode ? 'Hide code' : 'View code'}
            </button>
            <button
              onClick={() => {
                const blob = new Blob([svg], { type: 'image/svg+xml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'diagram.svg';
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Export SVG
            </button>
          </div>
        </div>
      </div>

      {showCode && (
        <pre className="bg-gray-50 border border-gray-200 rounded p-3 mt-2 text-xs overflow-x-auto">
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
}
